import { useMouseMove } from '@/hooks/dom/useMouseMove';
import { getImageFile, getModelFile } from '@/utils';
import { createProgress, lerp } from '@/utils/math';
import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { onUnmounted, ref, watch } from 'vue';
import { BALL_CENTER_DURATION, BALL_CENTER_POSITION, getBallCenterSize, BALL_ENTER_DURATION, BALL_INIT_POSITION, BALL_INIT_SIZE, BALL_MICRO_POSITION, BALL_MICRO_SIZE, LIGHT_NAME_LIST } from './config';
import { fragmentShader, vertexShader } from './shader';
import Particles from '@/three/particles';

const { deltaX, isMouseDown, setDeltaX, updateDelayDeltaX, delayDeltaX } = useMouseMove();

let model: THREE.Group<THREE.Object3DEventMap>;
export async function loadModel() {
    const loader = new GLTFLoader();
    await new Promise<void>(r => {
        loader.load(getModelFile('ball-processed.glb'), (gltf: GLTF) => {
            model = gltf.scene;
            r();
        });
    })
}

export function useBall(scene: THREE.Scene, option: { onRotate: (degree: number) => void }) {
    const show = ref(false);
    const modelSize = ref(BALL_INIT_SIZE);
    const lightOpacity = ref(-1);
    // let model: THREE.Group<THREE.Object3DEventMap>;
    const { onRotate } = option;

    function putBallAtScene() {
        model.position.copy(BALL_INIT_POSITION);
        model.scale.set(BALL_INIT_SIZE, BALL_INIT_SIZE, BALL_INIT_SIZE);
        hideLight();
        scene.add(model);
    }

    function resetModel() {
        model.position.copy(BALL_INIT_POSITION);
        model.scale.set(BALL_INIT_SIZE, BALL_INIT_SIZE, BALL_INIT_SIZE);
    }

    function createBindModelEvent(camera: THREE.Camera, cb: (list: THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>[]) => void) {
        return function (event) {
            if (!event) return;
            const raycaster = new THREE.Raycaster();
            const mouse = new THREE.Vector2();
            mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
            mouse.y = - (event.clientY / window.innerHeight) * 2 + 1;
            raycaster.setFromCamera(mouse, camera);
            const list = raycaster.intersectObjects(scene.children);
            cb(list);
        }
    }

    function showByMicro() {
        show.value = true;
        const update = createProgress(BALL_ENTER_DURATION);
        function animate(timestamp: number) {
            const progress = update(timestamp);
            modelSize.value = lerp(BALL_INIT_SIZE, BALL_MICRO_SIZE, progress);
            const position = BALL_INIT_POSITION.clone().lerp(BALL_MICRO_POSITION, progress);
            model.position.copy(position);
            // const opacity = lerp(0.5, 1, progress);
            // setModelOpacity(opacity);
            if (progress < 1) requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
    }

    function setModelOpacity(opacity: number) {
        model.traverse(child => {
            if (child instanceof THREE.Mesh) {
                const material = child.material as THREE.MeshBasicMaterial; // 类型断言
                material.opacity = opacity;
                material.transparent = opacity < 1; // 设置透明属性
            }
        })
    }

    async function moveToCenter() {
        const update = createProgress(BALL_CENTER_DURATION);
        await new Promise<void>(r => {
            function animate(timestamp: number) {
                const progress = update(timestamp);
                const position = BALL_MICRO_POSITION.clone().lerp(BALL_CENTER_POSITION, progress);
                modelSize.value = lerp(BALL_MICRO_SIZE, getBallCenterSize(), progress);
                model.position.copy(position);
                if (progress < 1) {
                    requestAnimationFrame(animate)
                } else {
                    requestAnimationFrame(rotate);
                    r();
                }
            };
            requestAnimationFrame(animate);
        })
    }
    watch(modelSize, (newVal) => model.scale.set(newVal, newVal, newVal));

    function rotate() {
        // if (Math.abs(deltaX.value) < 0.001 && !isMouseDown.value) model.rotateY(0.001);
        const yRotation = model.rotation.y;
        const yRotationDegree = THREE.MathUtils.radToDeg(yRotation);
        onRotate(yRotationDegree);
        // if (yRotationDegree < -45 && deltaX.value < 0) return stop();
        // if (yRotationDegree > 45 && deltaX.value > 0) return stop();
        model.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), deltaX.value);
        setDeltaX(deltaX.value / 1.02);
        updateDelayDeltaX();
        bgInBallLookAtCamera();
        requestAnimationFrame(rotate);
        
        function stop() {
            setDeltaX(0);
            requestAnimationFrame(rotate);
        }
    }

    /** 中间的一个白色的背景始终朝向相机 */
    function bgInBallLookAtCamera() {
        const a = scene.getObjectByName('ball');
        const b = scene.getObjectByName('Sphere014001');

        if (!a || !b) return;

        b.lookAt(0, 0, 10);
    }

    function rotateZ(duration: number, angle = -Math.PI * 2) {
        const update = createProgress(duration);
        function animate(time: number) {
            const progress = update(time);
            model.rotation.z = lerp(0, angle, progress);
            if (progress < 1) requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
    }

    function renderParticles() {
        const geometry = new THREE.BufferGeometry();
        const material = new THREE.ShaderMaterial({
            uniforms: {
                pointTexture: { value: new THREE.TextureLoader().load(getImageFile('dot2.png')) }
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            blending: THREE.AdditiveBlending
        });
        const system = new Particles();
        const particles = new THREE.Points(geometry, material);
        const animate = (timestamp: number) => {
            const info = system.animateAndInfo(timestamp);
            for (let key in info) {
                geometry.setAttribute(key, info[key]);    
            }
            if (Math.abs(deltaX.value) < 0.0001 && !isMouseDown.value) particles.rotateY(0.001);
            particles.rotateY(delayDeltaX.value);
            requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
        scene.add(particles);
    }    

    function initOriginLightOpacity() {
        LIGHT_NAME_LIST
            .map(name => model.getObjectByName(name) as THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>)
            .forEach(item => {
                console.log(item);
            })
    }
    
    function initOriginLightLayer() {
        // return;
        scene.traverse(item => item.layers.mask = 1)
        LIGHT_NAME_LIST
            .map(name => model.getObjectByName(name) as THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>)
            .forEach(item => {
                item.layers.mask = 2;
            })
    }

    function getLightList() {
        return LIGHT_NAME_LIST
            .map(name => model.getObjectByName(name) as THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>)
            .filter(item => item);
    }

    watch(lightOpacity, (newVal) => {
        getLightList().forEach(light => light.material.opacity = newVal);
    });

    function hideLight() {
        // initOriginLightOpacity();
        initOriginLightLayer();
        lightOpacity.value = 0;
    }

    function showLight() {
        function animate() {
            requestAnimationFrame(animate);
            if (lightOpacity.value < 1) lightOpacity.value += 0.005;
        }
        requestAnimationFrame(animate);
    }

    onUnmounted(() => {
        model.rotation.x = 0;
        model.rotation.y = 0;
        model.rotation.z = 0;
    });

    return {
        showLight,
        // loadModel,
        putBallAtScene,
        showByMicro,
        moveToCenter,
        renderParticles,
        createBindModelEvent,
        getLightList,
        resetModel,
        rotateZ,
    }
}